import React, { useEffect, useState } from 'react'
import { toastMessage } from 'utils/messages'

import { msConverter } from 'utils'
import { USER_STATES } from '../../../constants'
import { apiContentText, apiSchedules } from 'api'
import ConfigContainerUI from './ConfigContainerUI'
import { lang } from 'utils/helpers'

const ConfigContainer = ({
  company, tag, fields, configurations,
  title, subtitle, onChange
}) => {
  const [data, setData] = useState({})
  const [currentData, setCurrentData] = useState({})
  const [schedules, setSchedules] = useState({})
  const [currentChannelPreview, setCurrentChannelPreview] = useState('')
  const [isPreviewMsgModalOpen, setIsPreviewMsgModalOpen] = useState(false)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const userStatesOptions = USER_STATES.expired.map(item => ({
    value: item,
    label: lang(`states_${item}`)
  }))

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed && tag) {
      setLoading(true)

      const withoutMsgConfig = ['users', 'operators']

      if (!withoutMsgConfig.includes(tag)) {
        if (fields.includes('message')) {
          apiContentText.getRichText(tag)
            .then(({ data, status }) => {
              if (status === 200) {
                const parsedData = {
                  id: data._id || '',
                  tag: data.tag || tag,
                  message: {
                    content: data.text?.default.content || '',
                    contentHtml: data.text?.default.contentHtml || '',
                    attachments: data.text?.default.attachments || [],
                    buttons: data.text?.default.buttons || [],
                    removeEmojisAndAccents: data.text?.default.removeEmojisAndAccents || false
                  },
                  timer: getTimer({
                    value: {
                      time: data.time ? data.time : tag === 'outOfOffice' ? 900000 : 0,
                      unit: data.time ? data.unit : tag === 'outOfOffice' ? 'minutes' : 'seconds'
                    }
                  })
                }
                setData(parsedData)
                setCurrentData(parsedData)

                if (fields.includes('schedule')) {
                  apiSchedules.get({
                    tag: [data.tag || tag],
                    group: [data.group || 'company'],
                    createdTo: [data.createdTo || company._id]
                  })
                    .then(result => setSchedules({ [data.tag || tag]: result[0] }))
                }
              }
            })
            .catch(() => setError({ message: lang('dashboard_error_fetch_default') }))
            .finally(() => setLoading(false))
        } else if (fields.includes('schedule')) {
          apiSchedules.get({
            tag: [data.tag || tag],
            group: [data.group || 'company'],
            createdTo: [data.createdTo || company._id]
          })
            .then(result => {
              setSchedules({ [data.tag || tag]: result[0] })
              setLoading(false)
            })
        }
      } else {
        if (configurations.data) {
          let parsedData = {
            activation: {
              isActive: configurations.data[tag]?.isActive || false
            },
            unassignOperators: {
              removeAssignedOperators: configurations.data[tag]?.removeAssignedOperators || false
            },
            timer: getTimer({
              value: {
                time: configurations.data[tag]?.sessionTime ? configurations.data[tag].sessionTime : tag === 'users' ? 1800000 : 900000
              }
            })
          }
          if (tag === 'users') parsedData = {
            ...parsedData,
            singleSelector: getSelector({ value: configurations.data[tag]?.newState }) || null,
            multipleSelector: getSelector({ value: configurations.data[tag]?.statesToApply }) || null
          }
          setData(parsedData)
          setCurrentData(parsedData)
        }
      }
    }

    return () => isSubscribed = false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations])

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed && Object.keys(currentData).length) onChange(currentData)

    return () => isSubscribed = false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData])

  const onTogglePreviewMsgModal = () => setIsPreviewMsgModalOpen(!isPreviewMsgModalOpen)
  const onToggleScheduleModal = () => setIsScheduleModalOpen(!isScheduleModalOpen)

  const getSelector = ({ value }) => {
    let selector = []
    const isArray = Array.isArray(value)

    if (isArray) {
      value.map(item => selector.push(userStatesOptions.find(state => state.value === item)))
    } else {
      selector = userStatesOptions.find(state => state.value === value)
    }

    return selector
  }

  const getTimer = ({ value }) => {
    let timer = {}

    if (value.unit) {
      timer = {
        ...value,
        time: msConverter({ value: value.time, target: value.unit })
      }
    } else {
      let unit = 'seconds'
      const minutes = 1000 * 60
      const hours = minutes * 60
      const days = hours * 24

      if (value.time >= days) {
        unit = 'days'
      } else if (value.time >= hours) {
        unit = 'hours'
      } else if (value.time >= minutes) {
        unit = 'minutes'
      }

      timer = {
        time: msConverter({ value: value.time, target: unit }),
        unit
      }
    }

    return timer
  }

  const handleErrors = (type, value) => {
    // Operators
    if (tag === 'operators' || tag === 'users') {
      // Valor ingresado debe ser mayor o igual a 10 minutos
      if (value?.unit === 'minutes' && value?.time < 10) {
        setError({
          tag,
          type,
          message: lang('dashboard_error-message-config-operators-time')
        })
      } else if (error) {
        setError(null)
      }
    }
  }

  const onChangeInner = ({ type, value }) => {
    // Errors
    handleErrors(type, value)

    let parsedValue = value
    if (type === 'activation') parsedValue = { isActive: value }
    if (type === 'unassignOperators') parsedValue = { removeAssignedOperators: value }

    setCurrentData(prevState => ({
      ...prevState,
      [type]: parsedValue
    }))
  }

  const handleSaveSchedule = async (data) => {
    return apiSchedules.post({
      schedules: [{ ...schedules[data.tag === 'office' ? 'outOfOffice': data.tag], group: 'company', schedule: data }]
    })
      .then(result => setSchedules(prevState => ({ ...prevState, [data.tag === 'office' ? 'outOfOffice': data.tag]: result[0] })))
      .catch(() => toastMessage(lang('schedules_error_saving'), 'error'))
  }

  return (
    <ConfigContainerUI
      company={company}
      fields={fields}
      tag={tag}
      title={title && lang(`configuration_messages_${tag}_containerTitle`)}
      subtitle={subtitle && lang(`configuration_messages_${tag}_containerSubtitle`)}
      schedule={schedules[tag]?.schedule}
      data={data}
      currentData={currentData}
      isPreviewMsgModalOpen={isPreviewMsgModalOpen}
      currentChannelPreview={currentChannelPreview}
      isScheduleModalOpen={isScheduleModalOpen}
      userStatesOptions={userStatesOptions}
      loading={tag === 'users' || tag === 'operators' ? configurations.loading : loading}
      error={tag === error?.tag ? error : null}
      onChange={onChangeInner}
      setCurrentChannelPreview={setCurrentChannelPreview}
      onTogglePreviewMsgModal={onTogglePreviewMsgModal}
      onToggleScheduleModal={onToggleScheduleModal}
      handleSaveSchedule={handleSaveSchedule}
    />
  )
}

export default ConfigContainer
